import { OCCASION_CONFIG } from "@listou/shared/constants";
import type { GroupSummary, List } from "@listou/shared/types/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { ListMiniCover } from "../components/list-mini-cover";
import { Spinner } from "../components/ui/spinner";
import { countdownLabel, formatDay, plural } from "../lib/format";
import { groupsQuery, listsQuery } from "../lib/queries";
import { errorMessage, viewerQuery } from "../lib/session";

export const Route = createFileRoute("/_authed/mes-listes/")({
  loader: ({ context: { queryClient } }) =>
    Promise.all([queryClient.prefetchQuery(listsQuery), queryClient.prefetchQuery(groupsQuery)]),
  component: DashboardPage,
});

function DashboardPage() {
  const viewer = useQuery(viewerQuery);
  const lists = useQuery(listsQuery);
  const groups = useQuery(groupsQuery);

  const active = lists.data?.lists.filter((list) => !list.archived) ?? [];
  const archived = lists.data?.lists.filter((list) => list.archived) ?? [];
  const firstName = viewer.data?.user.name.split(" ")[0];

  return (
    <div className="mx-auto grid max-w-6xl gap-8 px-4 py-8 sm:px-6 sm:py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="grid gap-1">
          <h1 className="text-4xl font-semibold sm:text-5xl">
            {firstName ? `Bonjour ${firstName}` : "Mes listes"}
          </h1>
          <p className="text-lg text-muted">Vos listes et celles que vous organisez à plusieurs.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/secret-santa/nouveau" className="btn-secondary min-h-12 px-6 text-lg">
            Organiser un Secret Santa
          </Link>
          <Link to="/groupes/nouveau" className="btn-secondary min-h-12 px-6 text-lg">
            Nouveau groupe de listes
          </Link>
          <Link to="/nouvelle-liste" className="btn min-h-12 px-6 text-lg">
            Nouvelle liste
          </Link>
        </div>
      </header>

      {groups.data && groups.data.groups.length > 0 && (
        <section className="grid gap-4">
          <h2 className="text-2xl font-semibold">Mes groupes</h2>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {groups.data.groups.map((group) => (
              <GroupCard key={group.id} group={group} />
            ))}
          </ul>
        </section>
      )}

      {lists.isPending && <Spinner className="size-8 justify-self-center" />}
      {lists.isError && (
        <p className="text-danger">
          {errorMessage(lists.error, "Vos listes n'ont pas pu être chargées.")}
        </p>
      )}

      {lists.isSuccess && active.length === 0 && (
        <div className="panel grid justify-items-start gap-4 p-8 sm:p-10">
          <h2 className="text-3xl font-semibold">Votre première liste vous attend</h2>
          <p className="max-w-[52ch] text-lg text-ink/80">
            Choisissez l'occasion, ajoutez quelques envies, puis partagez le lien à vos proches. Ils
            pourront réserver sans créer de compte.
          </p>
          <Link to="/nouvelle-liste" className="btn min-h-12 px-6 text-lg">
            Créer ma liste
          </Link>
        </div>
      )}

      {active.length > 0 && (
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {active.map((list) => (
            <ListCard key={list.id} list={list} />
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <section className="grid gap-4">
          <h2 className="text-2xl font-semibold text-muted">Archivées</h2>
          <ul className="grid gap-5 opacity-75 sm:grid-cols-2 lg:grid-cols-3">
            {archived.map((list) => (
              <ListCard key={list.id} list={list} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The organizer lands on the management page; a member on the group itself. */
function GroupCard({ group }: { group: GroupSummary }) {
  const countdown = countdownLabel(group.eventDate);
  const body = (
    <>
      <span className="font-display text-xl font-semibold group-hover:underline">
        {group.title}
      </span>
      <span className="text-muted">
        {group.mode === "secret_santa" ? "Secret Santa" : OCCASION_CONFIG[group.occasion].label}
        {" · "}
        {plural(group.placeCount, "personne", "personnes")}
        {countdown && ` · ${countdown}`}
      </span>
    </>
  );
  const className =
    "group grid gap-1 rounded-3xl border-[1.5px] border-line bg-surface p-5 transition hover:border-ink/25";
  return (
    <li>
      {group.isOrganizer ? (
        <Link to="/mes-groupes/$groupId" params={{ groupId: group.id }} className={className}>
          {body}
        </Link>
      ) : (
        <Link to="/g/$slug" params={{ slug: group.slug }} className={className}>
          {body}
        </Link>
      )}
    </li>
  );
}

function ListCard({ list }: { list: List }) {
  const occasion = OCCASION_CONFIG[list.occasion];
  const countdown = countdownLabel(list.eventDate);

  return (
    <li>
      <Link
        to="/mes-listes/$listId"
        params={{ listId: list.id }}
        className="group grid overflow-hidden rounded-3xl border-[1.5px] border-line bg-surface transition hover:border-ink/25"
      >
        <div className="relative">
          <ListMiniCover
            list={list}
            large
            className="h-40 transition group-hover:brightness-[1.03]"
          />
          {countdown && (
            <span className="absolute top-3 right-3 rounded-full bg-surface px-3 py-1 text-sm font-bold text-ink shadow-sm">
              {countdown}
            </span>
          )}
        </div>
        <div className="grid gap-2 p-5">
          <h2 className="font-display text-2xl font-semibold group-hover:underline">
            {list.title}
          </h2>
          <p className="text-muted">
            {occasion.label}
            {list.eventDate && `, le ${formatDay(list.eventDate)}`}
          </p>
          <div className="flex flex-wrap gap-2 pt-1 text-sm">
            <span className="rounded-full bg-ink/5 px-3 py-1 font-bold">
              {plural(list.wishCount, "envie", "envies")}
            </span>
            {list.reservedCount !== null && (
              <span className="rounded-full bg-fir/10 px-3 py-1 font-bold text-fir">
                {plural(list.reservedCount, "réservée", "réservées")}
              </span>
            )}
            {list.role === "editor" && (
              <span className="rounded-full bg-butter/50 px-3 py-1 font-bold">Vous aidez</span>
            )}
            {list.visibility === "private" && (
              <span className="rounded-full bg-ink/5 px-3 py-1 font-bold">Privée</span>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}
